import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import CampusGallery from "../components/CampusGallery";
import { EditableText, EditableImage } from "../components/live/Editable";
import { Award, Landmark, CalendarClock, PiggyBank, CheckCircle2 } from "lucide-react";

const AID_TYPES = [
  {
    icon: Award,
    title: "Scholarship & Awards Programme",
    desc: "Scholarship & awards come from a variety of sources such as the institute itself, reduction in fees, sponsorship by individuals, sponsorship by companies etc. A point based system is adopted to shortlist deserving candidates. For the Scholarships being offered go to the News & Events page.",
    extra: ["Means", "Merit", "Means & Merit", "Performance", "Special category (i.e. Early Enrollment, Defense, Female, Mechatronics, Out station, etc.)"],
    extraLabel: "The categories for scholarship are:",
    link: { to: "/news", label: "View News & Events" },
  },
  {
    icon: Landmark,
    title: "Loans",
    desc: "Students who gain admission may apply for loan to the designated PSU Banks of Punjab National Bank and Central Bank of India. Interested students may contact the Office of Financial Aid for more details.",
  },
  {
    icon: CalendarClock,
    title: "Monthly Payment Plans",
    desc: "Fees need to be paid as per the fee schedule provided. However, we understand that certain students find it difficult to pay fees in the prescribed payment period. To help parents of such students, Kiki Centre for Technology provides needy students a monthly payment plan option. Need based students may contact the Office of Financial Aid.",
  },
  {
    icon: PiggyBank,
    title: "Pre-Payment Plan",
    desc: "Parents have the possibility to pre-pay the entire 3½ year course fee at the first year rate. Those parents who are interested, may contact the Office of Financial Aid for more information.",
  },
];

const G = "/assets/kiki/gallery";
const SCHOLARSHIP_MOMENTS = [
  { src: `${G}/scholarship-ceremony-1.jpg`, caption: "State Skills Competition award at the Yuva Diwas Samaroh" },
  { src: `${G}/scholarship-cheque.jpg`, caption: "2nd-place State Skills Competition 2024 scholarship award" },
  { src: `${G}/scholarship-ceremony-2.jpg`, caption: "Scholarship cheque presented by the chief guest" },
  { src: `${G}/hwk-certificate-1.jpg`, caption: "Certificate awarded by HWK Koblenz, Germany" },
  { src: `${G}/hwk-certificate-2.jpg`, caption: "Internationally recognised HWK German certification" },
];

export default function FinancialAidPage() {
  return (
    <>
      <PageHero
        path="financial.hero"
        kicker="Financial Aid"
        title="Financial Aid"
        subtitle="Any deserving student, irrespective of financial constraints, should be able to pursue their career advancement goals at Kiki."
      />

      {/* Intro */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <EditableImage
            path="financial.main.image"
            src="/assets/kiki/financial-1.jpg"
            alt="Financial aid at Kiki Centre"
            className="rounded-3xl shadow-xl overflow-hidden"
            imgClassName="w-full h-full object-cover"
            data-testid="financial-main-image"
          />
          <div>
            <p className="kicker mb-3">Office of Financial Aid</p>
            <EditableText path="financial.intro.title" as="h2" className="text-3xl font-bold text-[#0B2A6B] mb-5 block">Helping you pay for your course</EditableText>
            <EditableText path="financial.intro.text1" as="p" multiline className="text-slate-600 leading-relaxed mb-4 block" data-testid="financial-intro-text">
              The Office of Financial Aid works on the principle that, any deserving student, irrespective of financial constraints, should be able to pursue their career advancement goals at Kiki.
            </EditableText>
            <EditableText path="financial.intro.text2" as="p" multiline className="text-slate-600 leading-relaxed mb-4 block">
              Applicants to the 3½ year courses may apply for financial aid. There are various types of financial aid and payment options possible to help you pay for the course. These include:
            </EditableText>
            <ul className="space-y-2.5">
              {["Scholarship & Awards Programme", "Loans", "Monthly Payment Plans", "Full Course Pre-payment plan"].map((t, i) => (
                <li key={t} className="flex items-center gap-2 text-slate-700 font-medium">
                  <CheckCircle2 className="h-5 w-5 text-yellow-500 shrink-0" />
                  <EditableText path={`financial.intro.option.${i}`} as="span">{t}</EditableText>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Aid types detail */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-7">
            {AID_TYPES.map((a, i) => (
              <div
                key={a.title}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-xl tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.07}s` }}
                data-testid={`financial-aid-card-${i}`}
              >
                <div className="h-14 w-14 rounded-xl bg-yellow-400 flex items-center justify-center mb-4">
                  <a.icon className="h-7 w-7 text-[#0B2A6B]" />
                </div>
                <EditableText path={`financial.aid.${i}.title`} as="h3" className="font-bold text-[#0B2A6B] text-xl mb-3 block">{a.title}</EditableText>
                <EditableText path={`financial.aid.${i}.desc`} as="p" multiline className="text-slate-600 leading-relaxed block">{a.desc}</EditableText>
                {a.extra && (
                  <div className="mt-4 rounded-xl bg-yellow-50 border border-yellow-100 p-4">
                    <p className="text-sm font-semibold text-[#0B2A6B] mb-2">{a.extraLabel}</p>
                    <ul className="space-y-1.5">
                      {a.extra.map((e, j) => (
                        <li key={e} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-yellow-500 shrink-0" />
                          <EditableText path={`financial.aid.${i}.extra.${j}`} as="span">{e}</EditableText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {a.link && (
                  <Link to={a.link.to} className="inline-block mt-4 text-yellow-600 font-bold text-sm hover:text-yellow-700">
                    {a.link.label} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scholarships in action */}
      <CampusGallery
        kicker="Scholarships in action"
        title="Supporting deserving students"
        subtitle="From institute scholarships and government skill awards to internationally recognised certifications — we help every deserving student move forward."
        images={SCHOLARSHIP_MOMENTS}
        bg="light"
        idPrefix="financial-gallery"
      />

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl bg-[#0B2A6B] text-white p-10">
            <EditableText path="financial.cta.title" as="h2" className="text-2xl font-bold mb-3 block">Speak to the Office of Financial Aid</EditableText>
            <EditableText path="financial.cta.text" as="p" multiline className="text-slate-300 mb-6 block">
              Not sure which option fits your family best? Our team will walk you through scholarships, loans and payment plans for the 3½ year courses.
            </EditableText>
            <div className="flex flex-wrap gap-3">
              <a href="tel:+918800288994" className="px-6 py-3 rounded-lg bg-yellow-400 text-[#0B2A6B] font-bold hover:bg-yellow-500 transition" data-testid="financial-call-btn">
                Call 8800288994
              </a>
              <Link to="/contact" className="px-6 py-3 rounded-lg border-2 border-white/40 text-white font-semibold hover:bg-white/10 transition">
                Contact Us
              </Link>
            </div>
          </div>
          <EditableImage
            path="financial.cta.image"
            src="/assets/kiki/financial-2.jpg"
            alt="Students at Kiki Centre"
            className="rounded-3xl shadow-xl overflow-hidden"
            imgClassName="w-full h-full object-cover"
          />
        </div>
      </section>
    </>
  );
}
